import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import logoFooter from "../assets/lemmon_icon.png";
 
import {
  obtenerObjetoConUsuarioByCode,
} from "../services/api";

// Helpers
import { getFieldsForActivar } from "../helpers/fieldsForActivar";
import { RECORD_FIELDS } from "../models/recordFields";
import { USER_FIELDS } from "../models/userFields";
import DynamicForm from "../components/forms/DynamicForm";
import SoporteActivacion from "../components/Soporte/SoporteActivacion";
import CodigoActivacion from "../components/CodigoActivacion"; // ✅ NUEVO

// Idioma
const lang = "es";
const texts = {
  es: {
    heading: "Activación de Registro",
    intro: `Hola, gracias por elegir LemmonTag para cuidar lo que más quieres.
            Si eres un usuario nuevo, llena este formulario con los datos requeridos.
            Si ya tienes una LemmonTag y quieres añadir esta a tu panel, solo selecciona "Ya tengo cuenta".`,
    loading: "Cargando...",
    notFound: "No hay datos para mostrar.",
    backToForm: "Volver al formulario",
  },
  en: {},
};

function Activar() {
  const [searchParams] = useSearchParams();
  const codigoAcceso = searchParams.get("activacion");

  const [internalView, setInternalView] = useState("activar");
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [registro, setRegistro] = useState(null);
  const [usuario, setUsuario] = useState({});
  
  // ✅ NUEVO: Estados para código de activación rascable
  const [codigoValidado, setCodigoValidado] = useState(false);
  const [tokenValidacion, setTokenValidacion] = useState(null);

  // 1) Cargar datos por código
  useEffect(() => {
    async function fetchData() {
      setCargando(true);
      setMensaje({ texto: "", tipo: "" });

      if (!codigoAcceso) {
        setMensaje({ texto: "No se proporcionó ?activacion=XYZ en la URL.", tipo: "error" });
        setCargando(false);
        return;
      }

      try {
        const res = await obtenerObjetoConUsuarioByCode(codigoAcceso);
        if (res.error || !res.objeto) {
          setMensaje({ texto: res.error || "No se encontró un registro con este código.", tipo: "error" });
        } else {
          setRegistro(res.objeto);
          setUsuario(res.usuario || {});
          
          // ✅ NUEVO: Si no tiene código de activación, marcar como validado automáticamente
          if (!res.objeto.codigoActivacionHash) {
            setCodigoValidado(true);
          }
        }
      } catch (err) {
        console.error("Error al obtener el registro:", err);
        setMensaje({ texto: "Ocurrió un error al obtener el registro.", tipo: "error" });
      }
      setCargando(false);
    }
    fetchData();
  }, [codigoAcceso]);

  // 2) Redirigir si ya está ACTIVO
  useEffect(() => {
    if (!cargando && registro?.estado === "activo") {
      if (registro.ownerId) {
        window.location.href = `/scan?code=${encodeURIComponent(registro.codigoAcceso)}&uid=${encodeURIComponent(registro.ownerId)}`;
      } else {
        window.location.href = "/userpanel";
      }
    }
  }, [cargando, registro]);

  // ✨ 3) Auto-detectar timezone del navegador
  useEffect(() => {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      setUsuario((prev) => ({
        ...prev,
        timezone: detectedTimezone || 'America/Mexico_City'
      }));
    } catch (error) {
      console.error('[Timezone] Error detectando:', error);
      // Fallback a México
      setUsuario((prev) => ({
        ...prev,
        timezone: 'America/Mexico_City'
      }));
    }
  }, []); 
    
  // 4) Memoizar los campos
  const fieldsToShow = useMemo(() => {
    const base = [...getFieldsForActivar(registro?.tipo || "mascota", internalView)];
    if (!base.some((f) => f.fieldKey === "aceptaTerminos")) {
      base.push({
        fieldKey: "aceptaTerminos",
        label: "Acepto los términos",
        inputType: "checkbox",
        required: true,
        helperText: (
          <>
            He leído y acepto el{" "}
            <a href="/privacidad" target="_blank" rel="noopener noreferrer">
              Aviso de Privacidad
            </a>{" "}
            y los{" "}
            <a href="/terminos" target="_blank" rel="noopener noreferrer">
              Términos de Uso
            </a>
            .
          </>
        ),
      });
    }
    return base;
  }, [registro?.tipo, internalView]);

  function handleDynamicFormChange(fieldKey, value) {
    // Campos de mascota/llavero
    const camposRegistro = ['nombre', 'raza', 'especie', 'fechaNacimiento', 'senasParticulares', 'alergias', 'descripcion'];

    if (RECORD_FIELDS[fieldKey] || camposRegistro.includes(fieldKey)) {
      setRegistro((prev) => ({ ...prev, [fieldKey]: value }));
      if (fieldKey === "cuentaExistente") {
        setInternalView(value ? "ligandoSiguiente" : "activar");
      }
    } else if (USER_FIELDS[fieldKey]) {
      setUsuario((prev) => ({ ...prev, [fieldKey]: value }));
    } else {
      console.warn("handleDynamicFormChange => Campo no reconocido:", fieldKey);
    }
  }

  // 5) Submit
  async function handleSubmit(formData) {
    if (!registro) return;

    // VALIDACIONES MEJORADAS: Mensajes específicos por estado
    if (registro.estado === "pendienteVerificacion") {
      setMensaje({
        texto: (
          <div>
            <h5 className="mb-3">Tu cuenta ya está creada</h5>
            <p>Revisa tu correo <strong>{usuario.correoPropietario || 'electrónico'}</strong> y haz clic en el enlace de verificación que te enviamos.</p>
            <hr />
            <p className="mb-2"><strong>¿No recibiste el email?</strong></p>
            <div className="d-flex gap-2 flex-wrap">
              <a href="/reenviar-verificacion" className="btn btn-primary">
                <i className="bi bi-envelope me-1"></i>Reenviar email
              </a>
              <a href="/login" className="btn btn-outline-secondary">
                <i className="bi bi-box-arrow-in-right me-1"></i>Iniciar sesión
              </a>
            </div>
          </div>
        ),
        tipo: "info",
      });
      return;
    }

    if (registro.estado === "pendientePorLigar") {
      setMensaje({
        texto: (
          <div>
            <h5 className="mb-3">Ya tienes una cuenta activa</h5>
            <p>Este tag está esperando ser vinculado a tu cuenta <strong>{usuario.correoPropietario || 'existente'}</strong>.</p>
            <hr />
            <p className="mb-2"><strong>¿Qué puedes hacer?</strong></p>
            <ul className="text-start mb-3">
              <li>Revisa tu correo y haz clic en el enlace que te enviamos</li>
              <li>O inicia sesión en tu panel para gestionar tus tags</li>
            </ul>
            <div className="d-flex gap-2 flex-wrap">
              <a href="/reenviar-verificacion" className="btn btn-primary">
                <i className="bi bi-envelope me-1"></i>Reenviar email
              </a>
              <a href="/login" className="btn btn-outline-secondary">
                <i className="bi bi-box-arrow-in-right me-1"></i>Iniciar sesión
              </a>
            </div>
          </div>
        ),
        tipo: "info",
      });
      return;
    }

    if (registro.estado !== "inactivo") {
      setMensaje({
        texto: (
          <div>
            <p className="mb-3">Este código ya ha sido procesado.</p>
            <a href="/login" className="btn btn-primary">
              <i className="bi bi-box-arrow-in-right me-1"></i>Ir a iniciar sesión
            </a>
          </div>
        ),
        tipo: "warning",
      });
      return;
    }

    if (!formData.aceptaTerminos) {
      setMensaje({
        texto: "Debes aceptar el Aviso de Privacidad y los Términos de Uso para continuar.",
        tipo: "error",
      });
      return;
    }

    try {
      const yaTengoCuenta = !!formData.cuentaExistente;
      const baseUrl = window.location.origin;

      if (yaTengoCuenta) {
        // ======== FLUJO: Ya tengo cuenta ========
        if (!usuario.correoPropietario) {
          setMensaje({ texto: "Por favor ingresa tu correo.", tipo: "error" });
          return;
        }

        const resp = await fetch(
          `/api/usuarios/porCorreo?email=${encodeURIComponent(usuario.correoPropietario)}`
        );
        const data = await resp.json();

        if (!data || data.error) {
          setMensaje({
            texto: "No encontramos una cuenta con ese correo. Si eres nuevo, desmarca la opción 'Ya tengo cuenta'.",
            tipo: "error",
          });
          return;
        }

        // Preparar datos de actualización
        const datosActualizacion = {
          estado: "pendientePorLigar",
          ownerId: data.data.id,
          codigoAcceso: registro.codigoAcceso,
          nombre: registro.nombre,
        };

        // ✅ NUEVO: Agregar token si fue validado con código rascable
        if (tokenValidacion) {
          datosActualizacion.tokenValidacion = tokenValidacion;
        }

        // Agregar campos específicos según tipo
        if (registro.tipo === 'mascota') {
          datosActualizacion.raza = registro.raza;
          datosActualizacion.especie = registro.especie;
          datosActualizacion.fechaNacimiento = registro.fechaNacimiento;
          datosActualizacion.senasParticulares = registro.senasParticulares;
          datosActualizacion.alergias = registro.alergias;
        } else if (registro.tipo === 'llavero') {
          datosActualizacion.descripcion = registro.descripcion;
        }

        const updateResp = await fetch(`/api/objetos/activar/${registro.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(datosActualizacion),
        });

        if (!updateResp.ok) {
          const error = await updateResp.json().catch(() => ({}));
          console.error("Error actualizando objeto:", error);
          setMensaje({ texto: error.error || "Error al preparar el objeto", tipo: "error" });
          return;
        }

        // Email de vinculación
        await fetch("/api/email/sendEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: usuario.correoPropietario,
            templateId: "ligar_nuevo_objeto_es",
            context: {
              nombrePropietario: data.data.nombrePropietario || "Usuario",
              nombreObjeto: registro.nombre || "Nuevo tag",
              validationLink: `${baseUrl}/verificar?activacion=${registro.codigoAcceso}`,
            },
          }),
        });

        setMensaje({
          texto: "¡Listo! Te enviamos un email para vincular este tag a tu cuenta existente. Revisa tu correo.",
          tipo: "success",
        });

      } else {
        // ======== FLUJO: Usuario nuevo ========
        if (!usuario.correoPropietario) {
          setMensaje({ texto: "Por favor ingresa tu correo.", tipo: "error" });
          return;
        }
        if (!usuario.nombrePropietario) {
          setMensaje({ texto: "Por favor ingresa tu nombre.", tipo: "error" });
          return;
        }

        // Verificar si ya existe
        const checkResp = await fetch(
          `/api/usuarios/porCorreo?email=${encodeURIComponent(usuario.correoPropietario)}`
        );
        const checkData = await checkResp.json();

        if (checkData && checkData.id) {
          setMensaje({
            texto: "Ya existe una cuenta con ese correo. Selecciona 'Ya tengo cuenta' para vincular este tag.",
            tipo: "error",
          });
          return;
        }

        // ✨ Crear usuario con timezone
        const newUserResp = await fetch("/api/usuarios", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            correoPropietario: usuario.correoPropietario,
            nombrePropietario: usuario.nombrePropietario || "",
            telefono: usuario.telefono || "",
            timezone: usuario.timezone || "America/Mexico_City",
            correoVerificado: false,
          }),
        });

        const newUserData = await newUserResp.json();
        if (newUserData.error) {
          setMensaje({ texto: newUserData.error, tipo: "error" });
          return;
        }

        // Preparar datos según el tipo
        const datosActualizacion = {
          estado: "pendienteVerificacion",
          ownerId: newUserData.id || newUserData.uid,
          codigoAcceso: registro.codigoAcceso,
          nombre: registro.nombre,
        };

        // ✅ NUEVO: Agregar token si fue validado con código rascable
        if (tokenValidacion) {
          datosActualizacion.tokenValidacion = tokenValidacion;
        }

        // Agregar campos específicos según tipo
        if (registro.tipo === 'mascota') {
          datosActualizacion.raza = registro.raza;
          datosActualizacion.especie = registro.especie;
          datosActualizacion.fechaNacimiento = registro.fechaNacimiento;
          datosActualizacion.senasParticulares = registro.senasParticulares;
          datosActualizacion.alergias = registro.alergias;
        } else if (registro.tipo === 'llavero') {
          datosActualizacion.descripcion = registro.descripcion;
        }

        const updateResp2 = await fetch(`/api/objetos/activar/${registro.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(datosActualizacion),
        });

        if (!updateResp2.ok) {
          const error = await updateResp2.json().catch(() => ({}));
          console.error("Error actualizando objeto (nuevo usuario):", error);
          setMensaje({ texto: error.error || "Error al preparar el objeto", tipo: "error" });
          return;
        }

        // Email de verificación
        await fetch("/api/email/sendEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: usuario.correoPropietario,
            templateId: "welcome_and_verify_es",
            context: {
              nombrePropietario: usuario.nombrePropietario || "Usuario",
              validationLink: `${baseUrl}/verificar?activacion=${registro.codigoAcceso}`,
            },
          }),
        });

        setMensaje({
          texto: "¡Cuenta creada exitosamente! Por favor revisa tu correo electrónico y haz clic en el enlace de verificación para activar tu cuenta y acceder al panel.",
          tipo: "success",
        });
      }
    } catch (err) {
      console.error("Error al guardar la activación:", err);
      setMensaje({ texto: "Ocurrió un error al guardar la activación.", tipo: "error" });
    }
  }

  // ────────────────────────────
  // RENDER
  // ────────────────────────────

  if (cargando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{texts[lang].loading}</span>
        </div>
      </div>
    );
  }

  if (!registro) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f8f8" }}>
        <div className="container my-4">
          <div className="card">
            <div className="card-body">
              <p>{mensaje.texto || texts[lang].notFound}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si ya está activo, el useEffect redirige
  if (registro.estado === "activo") {
    return null;
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f8f8" }}>
      
      <div className="container my-4">
        {/* Header Contextual según tipo */}
        {registro.tipo === 'mascota' && (
          <div className="alert alert-primary border-0 mb-4" 
     style={{
       borderRadius: 'var(--radius-lg)',
       boxShadow: 'var(--shadow-base)',
       padding: 'var(--space-4)'
     }} 
     role="alert">
            <div className="d-flex align-items-start">
              <div className="me-3" style={{fontSize: '2.5rem'}}>
                🐾
              </div>
              <div>
                <h5 className="alert-heading mb-2 fw-bold">Activando tu LemmonTag para Mascota</h5>
                <p className="mb-0">
                  Completa la información básica para proteger a tu compañero peludo. 
                  Podrás agregar más detalles después desde tu panel.
                </p>
              </div>
            </div>
          </div>
        )}

        {registro.tipo === 'llavero' && (
          <div className="alert alert-info border-0 shadow-sm mb-4" role="alert">
            <div className="d-flex align-items-start">
              <div className="me-3" style={{fontSize: '2.5rem'}}>
                🔑
              </div>
              <div>
                <h5 className="alert-heading mb-2 fw-bold">Activando tu LemmonTag para Objetos</h5>
                <p className="mb-0">
                  Dale un nombre identificador a este tag para tus llaves, mochila u otro objeto importante.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NUEVO: Verificar código de activación si existe y no ha sido validado */}
        {registro.codigoActivacionHash && !codigoValidado && (
          <CodigoActivacion
            objetoId={registro.id}
            onValidado={(token) => {
              setCodigoValidado(true);
              setTokenValidacion(token);
            }}
          />
        )}

        {/* Card con el formulario o el mensaje - Solo mostrar si no requiere código O ya fue validado */}
        {(!registro.codigoActivacionHash || codigoValidado) && (
        <div className="card shadow-sm">
          <div className="card-header bg-white border-bottom">
            <h1 className="h4 mb-0">{texts[lang].heading}</h1>
          </div>

          <div className="card-body">
            {mensaje.texto ? (
              <>
                <div className={`alert ${
                  mensaje.tipo === "error" ? "alert-danger" : 
                  mensaje.tipo === "info" ? "alert-info" : 
                  mensaje.tipo === "warning" ? "alert-warning" : 
                  "alert-success"
                }`}>
                  {mensaje.texto}
                </div>
                {mensaje.tipo === "error" && (
                  <button
                    onClick={() => setMensaje({ texto: "", tipo: "" })}
                    className="btn btn-primary mt-3"
                  >
                    {texts[lang].backToForm}
                  </button>
                )}
              </>
            ) : (
              <DynamicForm
                fields={fieldsToShow}
                recordData={{ ...registro, ...usuario }}
                view={internalView}
                onChange={handleDynamicFormChange}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
        )}
      </div>
      
     <SoporteActivacion tipoObjeto={registro?.tipo || "mascota"} />
      
      {/* ✅ FOOTER DISCRETO CON LOGO */}
      <footer style={{
        padding: 'var(--space-4) 0',
        textAlign: 'center',
        borderTop: `1px solid var(--gray-200)`,
        backgroundColor: 'var(--white)',
        marginTop: 'var(--space-8)'
      }}>
        <div className="container">
          <img 
            src={logoFooter} 
            alt="LemmonTags" 
            style={{
              height: '32px',
              opacity: 0.6,
              transition: 'var(--transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
          />
          <p style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--gray-500)',
            marginTop: 'var(--space-2)',
            marginBottom: 0
          }}>
            © {new Date().getFullYear()} LemmonTags. Protege lo que más te importa.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Activar;